import { Badge, Box, Flex, Input, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { FaTimes } from "react-icons/fa"

import { ProgramsService } from "@/client"
import type { ProgramPublic } from "@/client/types.gen"

export interface ProgramOption {
  id: string
  name: string
  status?: ProgramPublic["status"]
}

interface ProgramSelectorProps {
  selectedPrograms: ProgramOption[]
  onChange: (programs: ProgramOption[]) => void
}

const getStatusColor = (status?: string) => {
  switch (status) {
    case "active":
      return "green"
    case "inactive":
      return "orange"
    case "archived":
      return "gray"
    default:
      return "gray"
  }
}

export function ProgramSelector({
  selectedPrograms,
  onChange,
}: ProgramSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ["programs"],
    queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
    staleTime: 5 * 60 * 1000,
  })

  const allPrograms: ProgramPublic[] = data?.data ?? []

  const filteredPrograms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return allPrograms.filter((program) => {
      const matchesQuery = query
        ? program.name.toLowerCase().includes(query)
        : true
      const notSelected = !selectedPrograms.some(
        (selected) => selected.id === program.id,
      )
      return matchesQuery && notSelected
    })
  }, [allPrograms, searchQuery, selectedPrograms])

  const handleAddProgram = (program: ProgramPublic) => {
    onChange([
      ...selectedPrograms,
      { id: program.id, name: program.name, status: program.status },
    ])
    setSearchQuery("")
    setShowSuggestions(false)
  }

  const handleRemoveProgram = (programId: string) => {
    onChange(selectedPrograms.filter((program) => program.id !== programId))
  }

  return (
    <Box width="100%">
      <Box position="relative">
        <Input
          placeholder="Search or click to view all programs"
          value={searchQuery}
          onFocus={() => setShowSuggestions(true)}
          onChange={(event) => {
            setSearchQuery(event.target.value)
            setShowSuggestions(true)
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              return
            }
            event.preventDefault()
            event.stopPropagation()
            if (filteredPrograms.length > 0) {
              handleAddProgram(filteredPrograms[0])
            }
          }}
        />

        {showSuggestions && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            _dark={{ bg: "gray.800" }}
            borderWidth="1px"
            borderRadius="md"
            boxShadow="lg"
            maxH="300px"
            overflowY="auto"
            zIndex={10}
          >
            <VStack align="stretch" gap={0}>
              {isLoading ? (
                <Box px={3} py={2}>
                  <Text color="gray.500">Loading programs...</Text>
                </Box>
              ) : filteredPrograms.length > 0 ? (
                <>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    px={3}
                    py={2}
                    borderBottomWidth="1px"
                  >
                    {searchQuery.trim()
                      ? "Matching Programs"
                      : "Available Programs"}
                  </Text>
                  {filteredPrograms.map((program) => (
                    <Box
                      key={program.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: "gray.100", _dark: { bg: "gray.700" } }}
                      onClick={() => handleAddProgram(program)}
                    >
                      <Flex justify="space-between" align="center">
                        <Text>{program.name}</Text>
                        <Badge
                          colorPalette={getStatusColor(program.status)}
                          size="sm"
                        >
                          {program.status ?? "active"}
                        </Badge>
                      </Flex>
                    </Box>
                  ))}
                </>
              ) : (
                <Box px={3} py={2}>
                  <Text color="gray.500">
                    {searchQuery.trim()
                      ? "No matching programs."
                      : "No programs available."}
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        )}
      </Box>

      {showSuggestions && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={9}
          onClick={() => setShowSuggestions(false)}
        />
      )}

      {selectedPrograms.length > 0 && (
        <Flex gap={2} wrap="wrap" mt={3}>
          {selectedPrograms.map((program) => (
            <Badge
              key={program.id}
              colorPalette={getStatusColor(program.status)}
              size="lg"
              py={1}
              px={2}
              display="flex"
              alignItems="center"
              gap={2}
            >
              {program.name}
              <button
                type="button"
                onClick={() => handleRemoveProgram(program.id)}
                style={{ cursor: "pointer", opacity: 1 }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.opacity = "0.7"
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.opacity = "1"
                }}
              >
                <FaTimes />
              </button>
            </Badge>
          ))}
        </Flex>
      )}
    </Box>
  )
}
